import * as yup from 'yup';
import moment from 'moment';

import { i18n as I18n } from 'utils/i18n';
import { createGetValidator } from 'utils/validation';
import { getMinDate, getMaxDate } from 'utils/booking';
import { BOOKING_STATES } from 'constants/bookings';

export const recurrenceTypes = ['daily', 'weekly', 'monthly'] as const;
export const dayRanges = ['days', 'weekdays'] as const;
export const monthRanges = ['nthDayOfMonth', 'nthWeekdayOfMonth'] as const;
export const rangeEndTypes = ['date', 'count'] as const;

const schema = yup.object({
  id: yup.number(),
  date: yup
    .date()
    .required(I18n.t('bookings.errors.date_not_selected'))
    .when(
      ['service', '$services', '$date', '$state'],
      (
        service,
        services = [],
        today = moment().toDate(),
        state = BOOKING_STATES.tentative,
        schema
      ) => {
        if (!service || state !== BOOKING_STATES.tentative) return schema;

        return schema.max(
          getMaxDate(
            today,
            services.find(({ id }) => id === service)
          ),
          ({ max }) => I18n.t('bookings.errors.max_date', { max })
        );
      }
    ),
  service: yup
    .number()
    .required(I18n.t('bookings.errors.service_not_selected')),
  host: yup.object({ id: yup.number() }),
  slots: yup.array().of(
    yup.object({
      startTime: yup.string(),
      endTime: yup.string(),
    })
  ),
  recurrence: yup
    .object()
    .when(
      ['date', 'service', '$services', '$date', '$state'],
      (
        date,
        service,
        services = [],
        today = moment().toDate(),
        state = BOOKING_STATES.tentative,
        schema,
        { value: recurrence }
      ) => {
        if (!recurrence.enabled || state !== BOOKING_STATES.tentative)
          return schema;

        return schema.shape({
          type: yup
            .string()
            .required()
            .label('Recurrence type') // TODO: i18n
            .oneOf(recurrenceTypes),
          enabled: yup.boolean(),
          checked: yup.boolean(),
          everyNumberOf: yup.number().min(1),
          dayRange: yup.string().oneOf(dayRanges),
          weekDays: yup.array().of(yup.string()),
          dayOfMonthRange: yup.string().oneOf(monthRanges),
          nthDayOfMonth: yup.number().min(1).max(31),
          weekNumber: yup.number(),
          weekDay: yup.string(),
          rangeEndType: yup.string().oneOf(rangeEndTypes),
          endDate: yup.date().when(['rangeEndType'], (rangeEndType, schema) => {
            if (rangeEndType !== 'date') return schema;

            return schema
              .min(getMinDate(date, recurrence), ({ min }) =>
                I18n.t('bookings.errors.min_end_date', { min })
              )
              .max(
                getMaxDate(
                  today,
                  services.find(({ id }) => id === service)
                ),
                ({ max }) => I18n.t('bookings.errors.max_end_date', { max })
              );
          }),
          count: yup
            .number()
            .when(
              ['rangeEndType', 'type', 'dayRange', 'everyNumberOf'],
              (
                rangeEndType,
                recurrenceType,
                dayRange,
                everyNumberOf = 1,
                schema
              ) => {
                if (rangeEndType === 'date') return schema;

                const minDate = getMinDate(date, recurrence);
                const maxDate = getMaxDate(
                  today,
                  services.find(({ id }) => id === service)
                );

                const maxOccurrences = {
                  daily: Math.ceil(
                    (moment(maxDate).diff(moment(minDate), 'days', true) *
                      (dayRange === 'weekdays' ? 5 / 7 : 1)) /
                      everyNumberOf
                  ),
                  weekly: Math.ceil(
                    moment(maxDate).diff(moment(minDate), 'weeks', true) /
                      everyNumberOf
                  ),
                  monthly: Math.ceil(
                    moment(maxDate).diff(moment(minDate), 'months', true) /
                      everyNumberOf
                  ),
                };

                return schema
                  .required(I18n.t('bookings.errors.invalid_occurrences'))
                  .typeError(I18n.t('bookings.errors.invalid_occurrences'))
                  .integer(I18n.t('bookings.errors.invalid_occurrences'))
                  .min(2, ({ min }) =>
                    I18n.t('bookings.errors.min_occurrences', { min })
                  )
                  .max(maxOccurrences[recurrenceType], ({ max }) =>
                    I18n.t('bookings.errors.max_occurrences', { max })
                  );
              }
            ),
        });
      }
    ),
});

export default schema;

export const getValidator = createGetValidator(schema);
